import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
} from '@mui/material'
import React, { type FC, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Markup } from 'interweave'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import ReactGA from 'react-ga4'
import InputTextField from '../../../components/InputTextField'
import { t2cCurrencies, T2cInitSession, t2cMoneyRemittance } from '../../../utils/api'
import MaskInput from '../../../components/MaskInput'
import ReguliT2C from '../../../assets/pdf/reguli_T2C.pdf'
// import ReguliT2CRu from '../../../assets/pdf/reguli_T2C_ru.pdf'
// import ReguliT2CRo from '../../../assets/pdf/reguli_T2C_ro.pdf'
import Colors from '../../../utils/colors'
import PDFViewer from '../../../components/PDFViewer'

interface T2CFormProps {
  className?: string
  setOperationId: (id: string) => void
  error?: string
  formik: any
  InputProps?: any
  isLoading?: boolean
  showMessage4Ui?: boolean
}

interface IZalotayaKoronaIframeProps {
  loading: boolean
  setLoading: (loading: boolean) => void
}

const ZalotayaKoronaIframe: FC<IZalotayaKoronaIframeProps> = ({ loading, setLoading }) => {
  return (
    <>
      {loading && (
        <div className='flex justify-center items-center  bg-white'>
          <CircularProgress color='success' />
        </div>
      )}
      <div className='flex items-center justify-center bg-white w-full'>
        <iframe
          src='https://koronapay.com/transfers/online/receive/maib/?locale=ro'
          name='myiFrame'
          allowFullScreen
          marginHeight={0}
          marginWidth={0}
          frameBorder={0}
          title='ZalotayaKorona'
          loading='lazy'
          height='100%'
          width='100%'
          onLoad={() => {
            setLoading(false)
          }}
          onError={() => {
            setLoading(false)
          }}
          className='!overflow-scroll border-0 min-h-[550px] max-w-[400px]'
        />
      </div>
    </>
  )
}

export const T2CForm: React.FC<T2CFormProps> = ({
  formik,
  setOperationId,
  InputProps,
  showMessage4Ui,
  error,
  className,
  isLoading,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [loading, setLoading] = React.useState(false)
  const queryOptions = {
    refetchOnMount: true,
  }
  const {
    data: OperationId,
    isError,
    isLoading: isLoadingOperationId,
  } = useQuery(
    ['getT2cOperationId'],
    () =>
      T2cInitSession().then((r) => {
        ReactGA.event({
          category: 'T2C',
          action: 'T2CInitSession',
          label: 'T2CInitSession',
        })
        setOperationId(r.result.sessionId)
        return r
      }),
    queryOptions,
  )

  const id = OperationId?.result?.sessionId

  const {
    data: Providers,
    isError: ProvidersError,
    isLoading: isLoadingProviders,
  } = useQuery(['getT2cProviders'], () => t2cMoneyRemittance(id), {
    ...queryOptions,
    enabled: !!id,
  })

  const { data: Currencies, isError: CurrenciesError } = useQuery(
    ['getT2cCurrencies'],
    () => t2cCurrencies(id),
    {
      ...queryOptions,
      enabled: !!id,
    },
  )
  // useEffect(() => {
  //     if (id) {
  //         setOperationId(id);
  //     }
  // }, [id]);

  if (isError || ProvidersError || CurrenciesError || Providers?.errors) {
    navigate('/error', { state: { location: window.location.pathname } })
  }

  const termsByLanguage = () => {
    // const language = localStorage.getItem('i18nextLng')
    // if (language === 'ro') {
    //   return ReguliT2CRo
    // }
    // if (language === 'ru') {
    //   return ReguliT2CRu
    // }
    return ReguliT2C
  }

  return (
    <>
      {(isLoading || isLoadingProviders || isLoadingOperationId) && (
        <div className='flex justify-center items-center absolute top-0 left-0 right-0 bottom-0 z-50 bg-white bg-opacity-50 backdrop-filter backdrop-blur-sm'>
          <CircularProgress color='success' />
        </div>
      )}
      <form onSubmit={formik.handleSubmit} className={className}>
        <div className='flex flex-col items-center sm:mb-10 mb-4'>
          <h1 className='text-2xl font-bold'>{t('t2c-page.mainForm.title')}</h1>
          <Markup
            className='text-sm text-gray-500 text-center mt-4'
            content={t('t2c-page.mainForm.description')}
          />
        </div>
        <FormControl error={formik.errors.provider} fullWidth sx={{ marginY: '10px' }}>
          <Select
            className='w-full bg-white my-[10px]'
            labelId='provider'
            id='provider'
            placeholder='Provider'
            value={formik.values.provider}
            label={t('t2c-page.mainForm.provider-title')}
            error={formik.errors.provider}
            sx={{
              '&.MuiOutlinedInput-root': {
                marginY: 0,
                '& fieldset': {
                  borderColor: formik.errors.provider && 'red',
                },
                '&:hover fieldset': {
                  borderColor: formik.errors.provider ? 'red' : Colors.primary.secondary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: formik.errors.provider ? 'red' : Colors.primary.secondary,
                },
              },
            }}
            color='secondary'
            onChange={(e) => {
              formik.setFieldValue('provider', e.target.value)
            }}
          >
            {formik.values.provider === 0 && (
              <MenuItem value={0} disabled>
                {t('t2c-page.mainForm.system-transfer')}
              </MenuItem>
            )}

            {Providers?.result?.map((provider: any) => {
              if (provider.isActive === true) {
                return (
                  <MenuItem value={provider.dbId} key={provider.dbId}>
                    {provider.imtSpName}
                  </MenuItem>
                )
              }

              return null
            })}
          </Select>
          <FormHelperText>{t(formik.errors.provider)}</FormHelperText>
        </FormControl>
        {Providers?.result.some(
          (item: any) => item.dbId === formik.values.provider && item.imtSpAlias === 'ZK.IN',
        ) ? (
          ZalotayaKoronaIframe({ loading, setLoading })
        ) : (
          <>
            <InputTextField
              label={t('t2c-page.mainForm.number-title')}
              name='code'
              inputProps={InputProps}
              className='bg-white'
              onBlur={(e) => {
                formik.handleBlur(e)
              }}
              onKeyDown={(e) => {
                if (
                  formik.values.provider !== 10 &&
                  formik.values.provider !== 11 &&
                  formik.values.provider !== 1009 &&
                  formik.values.provider !== 1010
                )
                  if (e.key === 'e' || e.key === '-' || e.key === '+' || e.key === 'E') {
                    e.preventDefault()
                  }
              }}
              value={formik.values.code}
              error={formik.touched?.code && formik.errors.code}
              onChange={(e) => {
                if (
                  formik.values.provider === 10 ||
                  formik.values.provider === 11 ||
                  formik.values.provider === 1009 ||
                  formik.values.provider === 1010
                )
                  formik.setFieldValue('code', e.target.value)
                else {
                  const re = /^[0-9\b]+$/
                  if (re.test(e.target.value) || e.target.value === '') {
                    formik.setFieldValue('code', e.target.value)
                  }
                }
              }}
            />
            <InputTextField
              label={t('p2p-page.mainForm.amount')}
              name='amount'
              type='number'
              dropdown
              error={formik.touched?.amount && formik.errors.amount}
              onWheel={(event) => event.currentTarget.querySelector('input')?.blur()}
              className='appearance-none bg-white'
              onChange={(e) => {
                const re = /^[ \d,]+(\.\d*)?$/
                if (
                  (re.test(String(parseFloat(e.target.value))) || e.target.value === '') &&
                  e.target.value.toString().length < 10
                ) {
                  formik.setFieldValue('amount', parseFloat(e.target.value))
                }
              }}
              value={formik.values.amount || ''}
              onKeyDown={(e) => {
                if (e.key === 'e' || e.key === '-' || e.key === '+' || e.key === 'E') {
                  e.preventDefault()
                }
              }}
              onBlur={(e) => {
                formik.handleBlur(e)
              }}
              rightIcon={
                <Select
                  sx={{
                    '#currency': {
                      color: '#000',
                      borderLeft: '1px solid #e5e7eb',
                      borderRadius: '0px',
                      lineHeight: '17px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '0px !important',
                    },
                    ':root': {
                      '& .Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderWidth: '0px',
                        },
                      },
                    },
                  }}
                  labelId='currency'
                  id='currency'
                  value={formik.values.currency}
                  onChange={(e) => {
                    formik.setFieldValue('currency', e.target.value)
                  }}
                >
                  {Currencies?.result?.map((currency: any) => (
                    <MenuItem value={currency.id} key={currency.id}>
                      {currency.charCode}
                    </MenuItem>
                  ))}
                </Select>
              }
            />
            <InputTextField
              label={t('t2c-page.mainForm.fiscal-code-title')}
              id='idnp'
              name='idnp'
              value={formik.values.idnp}
              onChange={(e) => {
                formik.setFieldValue('idnp', e.target.value)
              }}
              onBlur={(e) => {
                formik.handleBlur(e)
              }}
              error={formik.touched.idnp && formik.errors.idnp}
            />
            <MaskInput
              // label={t("t2c-page.mainForm.number-card-title")}
              id='cardNumber'
              placeholder={t('t2c-page.mainForm.number-card-title')}
              mask='•••• •••• 9999 9999'
              className='bg-white'
              errorString={formik.touched.cardNumber && formik.errors.cardNumber}
              onBlur={(e) => {
                formik.handleBlur(e)
              }}
              maskPlaceholder='_'
              name='cardNumber'
              value={formik.values.cardNumber}
              onChange={(e) => {
                formik.setFieldValue('cardNumber', e.target.value.split(' ').join(''))
              }}
            />
            <div className='flex items-center'>
              <div className='flex items-center mb-4 '>
                <Checkbox
                  checked={formik.values.agree}
                  onChange={(e) => formik.setFieldValue('agree', e.target.checked)}
                  name='agree'
                  color='secondary'
                  className='rounded-xl !ml-0 !pl-0'
                />
                <span className='text-gray font-nexaLight text-[14px] '>
                  {`${t('t2c-page.mainForm.agree')} `}
                  <PDFViewer
                    className={
                      '!text-secondary' +
                      ' font-nexaLight hover:no-underline duration-100 ease-in underline text-[14px] cursor-pointer '
                    }
                    pdf={termsByLanguage()}
                    name={t('t2c-page.mainForm.terms')}
                  />
                </span>
              </div>
            </div>
            <Button
              variant='outlined'
              type='submit'
              disabled={!formik.values.agree}
              className='w-full text-white border-2 border-gray '
            >
              {t('p2p-page.mainForm.send_money')}
            </Button>
            {error && (
              <div className='text-red-500 text-center mt-4'>
                {!showMessage4Ui ? t(`${error}`) : error}
              </div>
            )}
          </>
        )}
      </form>
    </>
  )
}

export default memo(T2CForm)
